import { BaseService } from "./base.service";
import {
  CreateMedicineItemPayload,
  MedicineInfo,
  MedicineItemType,
  PillboxHistoryEntry,
  PillboxItem,
  PillboxPendingChange,
  ScheduleDay,
} from "@/types/pillbox";

function itemTypeParam(itemType: MedicineItemType): string {
  return itemType === MedicineItemType.SUPPLEMENT ? "supplements" : "medicines";
}

class PillboxService extends BaseService {
  constructor() {
    super("/pillbox");
  }

  getLedger(memberId: string, status?: string): Promise<PillboxItem[]> {
    const query = status ? `?status=${status}` : "";
    return this.get<PillboxItem[]>(`/${memberId}${query}`);
  }

  getSchedule(
    memberId: string,
    startDate?: string,
    days?: number
  ): Promise<ScheduleDay[]> {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (days) params.set("days", String(days));
    const query = params.toString() ? `?${params.toString()}` : "";
    return this.get<ScheduleDay[]>(`/${memberId}/schedule${query}`);
  }

  addMedicine(
    memberId: string,
    itemType: MedicineItemType,
    payload: CreateMedicineItemPayload
  ): Promise<PillboxItem> {
    return this.post<PillboxItem>(
      `/${memberId}/${itemTypeParam(itemType)}`,
      payload
    );
  }

  getItem(
    memberId: string,
    itemType: MedicineItemType,
    itemId: string
  ): Promise<PillboxItem> {
    return this.get<PillboxItem>(
      `/${memberId}/${itemTypeParam(itemType)}/${itemId}`
    );
  }

  getHistory(
    memberId: string,
    itemType: MedicineItemType,
    itemId: string
  ): Promise<PillboxHistoryEntry[]> {
    return this.get<PillboxHistoryEntry[]>(
      `/${memberId}/${itemTypeParam(itemType)}/${itemId}/history`
    );
  }

  updateItem(
    memberId: string,
    itemType: MedicineItemType,
    itemId: string,
    payload: CreateMedicineItemPayload
  ): Promise<PillboxItem> {
    return this.patch<PillboxItem>(
      `/${memberId}/${itemTypeParam(itemType)}/${itemId}`,
      payload
    );
  }

  resumeItem(
    memberId: string,
    itemType: MedicineItemType,
    pastItemId: string,
    payload: CreateMedicineItemPayload
  ): Promise<PillboxItem> {
    return this.post<PillboxItem>(
      `/${memberId}/${itemTypeParam(itemType)}/${pastItemId}/resume`,
      payload
    );
  }

  listPending(memberId: string): Promise<PillboxPendingChange[]> {
    return this.get<PillboxPendingChange[]>(`/${memberId}/pending`);
  }

  confirmPending(
    memberId: string,
    pendingId: string,
    edits: CreateMedicineItemPayload
  ): Promise<PillboxItem> {
    return this.patch<PillboxItem>(
      `/${memberId}/pending/${pendingId}/confirm`,
      { edits }
    );
  }

  rejectPending(memberId: string, pendingId: string): Promise<void> {
    return this.patch<void>(`/${memberId}/pending/${pendingId}/reject`);
  }

  stopItem(
    memberId: string,
    itemType: MedicineItemType,
    itemId: string
  ): Promise<PillboxItem> {
    return this.post<PillboxItem>(
      `/${memberId}/${itemTypeParam(itemType)}/${itemId}/stop`,
      {}
    );
  }

  deleteItem(
    memberId: string,
    itemType: MedicineItemType,
    itemId: string
  ): Promise<void> {
    return this.delete<void>(
      `/${memberId}/${itemTypeParam(itemType)}/${itemId}`
    );
  }

  toggleRefill(
    memberId: string,
    itemType: MedicineItemType,
    itemId: string,
    needsRefill: boolean
  ): Promise<PillboxItem> {
    return this.patch<PillboxItem>(
      `/${memberId}/${itemTypeParam(itemType)}/${itemId}/refill`,
      { needsRefill }
    );
  }

  getShoppingList(): Promise<PillboxItem[]> {
    return this.get<PillboxItem[]>("/shopping-list");
  }

  getMedicineInfo(name: string): Promise<MedicineInfo> {
    return this.get<MedicineInfo>(
      `/medicine-info?name=${encodeURIComponent(name)}`
    );
  }

  downloadSharePdf(
    memberId: string,
    view: "schedule" | "list",
    date?: string
  ): Promise<Blob> {
    const params = new URLSearchParams({ format: "pdf", view });
    if (date) params.set("date", date);
    return this.get<Blob>(`/${memberId}/share?${params.toString()}`, {
      responseType: "blob",
    });
  }

  downloadShoppingListPdf(): Promise<Blob> {
    return this.get<Blob>("/shopping-list/pdf", { responseType: "blob" });
  }
}

export default new PillboxService();
